import type {
  AutomationAccountOverride,
  AutomationDProp,
  AutomationSegment,
  AutomationTestDatumDto,
} from '@tms/shared';

/**
 * The Gradle command as a preview for people (TestRun src/utils/gradle.ts).
 * The server builds the real one itself (apps/api/src/modules/automation/gradle.ts);
 * these helpers follow it rule for rule, so the page shows what the runner gets.
 */

/** `auth.phone` → `AUTH_PHONE` (the rule .gitlab-ci.yml uses) */
export const ciVarFor = (key: string): string => key.toUpperCase().replace(/[.-]/g, '_');

/**
 * An account is set per class in the pre-flight, never as a global -D: the
 * server leaves these keys out of every run, so the preview does too.
 */
export const ACCOUNT_KEYS = ['auth.phone', 'auth.iin', 'auth.bin', 'auth.phoneCode'];

/** quote a -D value only when it holds shell-significant characters */
const quote = (value: string): string =>
  /[\s"'$&|;<>()]/.test(value) ? `"${value.replace(/"/g, '\\"')}"` : value;

/** Test data as -D properties, without empty, generated and CI-set values, and without the account keys. */
export const propsFromTestData = (rows: AutomationTestDatumDto[]): AutomationDProp[] =>
  rows
    .filter((row) => !row.dynamic && String(row.value ?? '').trim() !== '')
    // CI sets the value itself (-Dkey=$VARIABLE): not passed until someone changes it here
    .filter((row) => !(row.ciManaged && (row.updatedBy ?? 'scanner') === 'scanner'))
    .filter((row) => !ACCOUNT_KEYS.includes(row.key))
    .map((row) => ({
      key: row.key,
      value: String(row.value),
      ciVar: row.ciVar ?? ciVarFor(row.key),
      ...(row.wiredInCi === undefined ? {} : { wiredInCi: row.wiredInCi }),
    }));

/** `COUNTERPARTY` → `Counterparty` — the scanner's suite name for a tag. */
export const prettyTag = (tag: string): string =>
  tag.charAt(0) + tag.slice(1).toLowerCase().replace(/_/g, ' ');

/**
 * A suite name → its @Tag, for matching classes: a repository suite is a tag
 * shown through prettyTag; a suite added by hand has no tag and stays as it is.
 */
export const tagForSuite = (suite: string, domainTags: Array<{ tag: string }>): string =>
  domainTags.find((d) => prettyTag(d.tag) === suite)?.tag ?? suite;

/** The tag the server sends for a suite run: `Payment order` → `PAYMENT_ORDER`. */
export const rawTag = (name: string): string => name.toUpperCase().replace(/\s+/g, '_');

/**
 * -Dgroups is a JUnit tag expression; build.gradle joins it to the environment
 * tag as STAGE & (<expression>). Domains are «any of» (|), the segment is «and» (&).
 */
export const groupsExpression = (
  domainTags: string[] = [],
  segment: AutomationSegment | null = null,
): string | null => {
  const domains = domainTags.filter(Boolean);
  const any = domains.length > 1 ? `(${domains.join(' | ')})` : domains[0];
  const parts = [any, segment].filter(Boolean);
  return parts.length ? parts.join(' & ') : null;
};

/** A class's segment: from the scan, or by the IP / TOO / LLP tags of older scans. Both — none. */
export const segmentOfClass = (c: {
  segment?: AutomationSegment | null;
  tags?: string[];
}): AutomationSegment | null => {
  if (c.segment) return c.segment;
  const tags = c.tags ?? [];
  const ip = tags.includes('IP');
  const too = tags.includes('TOO') || tags.includes('LLP');
  if (ip === too) return null;
  return ip ? 'IP' : 'TOO';
};

export const SEGMENTS: AutomationSegment[] = ['IP', 'TOO'];

/** The command the GitLab Runner executes. */
export const buildGradleCommand = ({
  task,
  domainTags,
  segment,
  classes,
  props,
}: {
  task: string;
  domainTags?: string[];
  segment?: AutomationSegment | null;
  /** only these classes → --tests "*.Class" */
  classes?: string[] | null;
  props: AutomationDProp[];
}): string => {
  const parts = ['./gradlew', 'clean', task];
  const groups = groupsExpression(domainTags, segment ?? null);
  if (groups) parts.push(`-Dgroups=${quote(groups)}`);
  // the dot keeps "*Foo" from also taking LlpFoo
  for (const c of classes ?? []) parts.push('--tests', `"*.${c}"`);
  for (const p of props) parts.push(`-D${p.key}=${quote(p.value)}`);
  return parts.join(' ');
};

/** Per-class accounts → -Dauth.phone.<Class>=… */
export const accountOverrideProps = (overrides: AutomationAccountOverride[]): AutomationDProp[] => {
  const out: AutomationDProp[] = [];
  const add = (key: string, value: string) => out.push({ key, value, ciVar: ciVarFor(key) });
  for (const o of overrides) {
    if (o.phone) add(`auth.phone.${o.className}`, o.phone);
    if (o.iin) add(`auth.iin.${o.className}`, o.iin);
    if (o.bin) add(`auth.bin.${o.className}`, o.bin);
  }
  return out;
};

/** The two-line test-side change that makes a per-class `-Dauth.phone.<Class>` work. */
export const PREPROD_SNIPPET = `protected String phone() {
    return System.getProperty("auth.phone." + getClass().getSimpleName(),
           System.getProperty("auth.phone", defaultPhone()));
}
protected String iin() {
    return System.getProperty("auth.iin." + getClass().getSimpleName(),
           System.getProperty("auth.iin", defaultIin()));
}`;

export interface EnvTask {
  task: string;
  tag: string | null;
}

/** The environment of a run → the repository's Gradle task, as the server resolves it. */
export const resolveGradleTask = (env: string, envTasks: EnvTask[]): string => {
  // the exact tag first (stage_env: 'STAGE'), only then expressions like 'STAGE & TOO'
  const byTag = (tag: string) =>
    envTasks.find((e) => e.tag === tag)?.task ??
    envTasks.find((e) =>
      e.tag
        ?.split(/[^A-Z_]+/)
        .filter(Boolean)
        .includes(tag),
    )?.task;
  // «tests» usually has no includeTags and runs the WHOLE project, so it is the last resort
  const byName = (name: string) =>
    envTasks.find((e) => e.task.toLowerCase() === `${name.toLowerCase()}_env`)?.task;
  return (
    byTag(env) ??
    byName(env) ??
    (env === 'QA' ? (byTag('DEV') ?? byName('dev')) : undefined) ??
    (env === 'STAGE' ? (byTag('PREPROD') ?? byName('preprod')) : undefined) ??
    envTasks.find((e) => e.task === 'tests')?.task ??
    envTasks[0]?.task ??
    'test'
  );
};

/** What the scanner learnt about a connected repository (TestRun's SourceProject). */
export interface SourceProject {
  envTasks: EnvTask[];
  domainTags: Array<{ tag: string; count: number }>;
  /** IE / LLP by the IP and TOO tags; older scans lack it */
  segments?: Array<{ segment: AutomationSegment; count: number }>;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/** A project's `sourceProject` as the API stores it (a free-form record) → its typed shape; null — no repository. */
export const sourceProjectOf = (
  raw: Record<string, unknown> | null | undefined,
): SourceProject | null => {
  if (!raw) return null;
  const list = (value: unknown): Record<string, unknown>[] =>
    Array.isArray(value) ? value.filter(isRecord) : [];
  const envTasks = list(raw.envTasks)
    .map((e) => ({ task: String(e.task ?? ''), tag: typeof e.tag === 'string' ? e.tag : null }))
    .filter((e) => e.task);
  const domainTags = list(raw.domainTags)
    .map((d) => ({ tag: String(d.tag ?? ''), count: Number(d.count ?? 0) }))
    .filter((d) => d.tag);
  const segments = Array.isArray(raw.segments)
    ? list(raw.segments).flatMap((s) =>
        s.segment === 'IP' || s.segment === 'TOO'
          ? [{ segment: s.segment as AutomationSegment, count: Number(s.count ?? 0) }]
          : [],
      )
    : undefined;
  return { envTasks, domainTags, ...(segments ? { segments } : {}) };
};
